import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import BreadCrumb from "components/common/BreadCrumb";
import StatusBadge from "components/common/StatusBadge";
import PageLoadingSpinner from "components/common/PageLoader";
import DeleteModal from "components/common/DeleteModal";
import { DateOnly, DateTime } from "components/common/Date";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  ComplaintMaterialSelectionType,
  ComplaintMaterialWarrantyStatusLabel,
  complaintMaterialStatusMap,
  isComplaintMaterialCancelBlocked,
} from "features/ComplaintMaterials/constants/complaint-material.constants";
import type { ComplaintMaterial } from "features/ComplaintMaterials/types/complaint-material.types";
import {
  cancelComplaintMaterial,
  deleteComplaintMaterial,
  getComplaintMaterial,
} from "features/ComplaintMaterials/services/ComplaintMaterialService";

const LIST_PATH = "/complaint-materials";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

const ComplaintMaterialOverview = () => {
  document.title = getDocumentTitle("Complaint Material");
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAccess();

  const [record, setRecord] = useState<ComplaintMaterial | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getComplaintMaterial(Number(id));
      if (res.success && res.data) {
        setRecord(res.data);
      } else {
        toast.error(res.message || "Could not load record");
        navigate(LIST_PATH, { replace: true });
      }
    } catch (err) {
      console.warn(err);
      toast.error("Could not load record");
      navigate(LIST_PATH, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canUpdate = can(Permissions.COMPLAINT_MATERIAL.UPDATE);
  const canCancel = can(Permissions.COMPLAINT_MATERIAL.CANCEL);
  const canDelete = can(Permissions.COMPLAINT_MATERIAL.DELETE);

  if (loading) {
    return <PageLoadingSpinner message="Loading..." />;
  }
  if (!record) return null;

  const status = record.status;
  const cancelBlocked = isComplaintMaterialCancelBlocked(status);
  const showCancel = canCancel && !cancelBlocked;

  const itemLabel =
    record.selectionType === ComplaintMaterialSelectionType.MATERIAL
      ? (record.material?.name ?? "—")
      : (record.name?.trim() || "—");

  const handleCancel = async () => {
    setBusy(true);
    try {
      const res = await cancelComplaintMaterial(record.id, {
        reason: cancelReason.trim() || undefined,
      });
      if (res.success) {
        toast.success(res.message ?? "Cancelled.");
        await load();
        setCancelOpen(false);
        setCancelReason("");
      } else {
        toast.error(res.message ?? "Could not cancel.");
      }
    } catch (err) {
      toast.error(axiosMessage(err, "Could not cancel."));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteComplaintMaterial(record.id);
      if (res.success) {
        toast.success(res.message ?? "Deleted.");
        navigate(LIST_PATH, { replace: true });
      } else {
        toast.error(res.message ?? "Could not delete record.");
      }
    } catch (err) {
      toast.error(axiosMessage(err, "Could not delete record."));
    } finally {
      setDeleteOpen(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Complaint Material Details"
          pageTitle="Complaint Materials"
          items={[{ label: "Complaint Materials", path: LIST_PATH }]}
        />

        <Row>
          <Col lg={12}>
            <Card className="mt-n4 mx-n4">
              <div className="bg-light-subtle">
                <CardBody className="pb-3 px-4">
                  <Row className="mb-0">
                    <div className="col-md">
                      <div>
                        <h4 className="fw-bold">{itemLabel}</h4>
                        <div className="hstack gap-3 flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted">Status:</span>
                            <StatusBadge
                              value={status}
                              map={complaintMaterialStatusMap}
                            />
                          </div>
                          {record.created_at && (
                            <>
                              <div className="vr" />
                              <div>
                                Created:{" "}
                                <span className="fw-medium">
                                  <DateTime date={record.created_at} />
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-auto mt-3 mt-md-0">
                      <div className="hstack gap-2 flex-wrap justify-content-md-end">
                        {canUpdate ? (
                          <Link
                            to={`/complaint-materials/edit/${record.id}`}
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-pencil-line align-bottom me-1" />
                            Edit
                          </Link>
                        ) : null}
                        {showCancel ? (
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            onClick={() => setCancelOpen(true)}
                          >
                            Cancel
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => setDeleteOpen(true)}
                          >
                            <i className="ri-delete-bin-line align-bottom me-1" />
                            Delete
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </div>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                <div className="text-muted">
                  <h6 className="mb-3 text-uppercase">Item &amp; complaint</h6>
                  <div className="table-responsive">
                    <Table className="table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th className="ps-0" scope="row" style={{ width: "220px" }}>
                            Item source :
                          </th>
                          <td className="text-muted">
                            {record.selectionType === ComplaintMaterialSelectionType.MATERIAL
                              ? "Material from catalog"
                              : "Client-supplied item"}
                          </td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Item :
                          </th>
                          <td className="text-muted">{itemLabel}</td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Manufacturer :
                          </th>
                          <td className="text-muted">
                            {record.manufacturer || "—"}
                          </td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Serial no. :
                          </th>
                          <td className="text-muted">{record.serialNo || "—"}</td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Purchase date :
                          </th>
                          <td className="text-muted">
                            {record.purchaseDate ? (
                              <DateOnly date={record.purchaseDate} />
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Warranty status :
                          </th>
                          <td className="text-muted">
                            {ComplaintMaterialWarrantyStatusLabel[record.warrantyStatus]}
                          </td>
                        </tr>
                        <tr>
                          <th className="ps-0" scope="row">
                            Complaint received :
                          </th>
                          <td className="text-muted">
                            <DateOnly date={record.complaintReceivedDate} />
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>

                  <div className="pt-3 mt-4">
                    <h6 className="mb-3 text-uppercase">Complaint description</h6>
                    <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {record.complaintDescription}
                    </p>
                  </div>

                  {record.remarks ? (
                    <div className="pt-3 mt-4">
                      <h6 className="mb-3 text-uppercase">Remarks</h6>
                      <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {record.remarks}
                      </p>
                    </div>
                  ) : null}

                  {record.cancelReason ? (
                    <div className="pt-3 mt-4">
                      <h6 className="mb-3 text-uppercase text-danger">
                        Cancellation reason
                      </h6>
                      <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {record.cancelReason}
                      </p>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="mb-3">
              <CardBody>
                <h6 className="text-uppercase mb-3 text-muted">
                  Lifecycle dates
                </h6>
                <div className="vstack gap-3">
                  <div>
                    <p className="mb-1 text-uppercase fw-medium small">
                      Complaint received
                    </p>
                    <h6 className="mb-0 fs-14">
                      <DateOnly date={record.complaintReceivedDate} />
                    </h6>
                  </div>
                  <div>
                    <p className="mb-1 text-uppercase fw-medium small">
                      Resolved
                    </p>
                    <h6 className="mb-0 fs-14">
                      {record.resolvedDate ? (
                        <DateOnly date={record.resolvedDate} />
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </h6>
                  </div>
                  <div>
                    <p className="mb-1 text-uppercase fw-medium small">
                      Dispatched
                    </p>
                    <h6 className="mb-0 fs-14">
                      {record.dateOfDispatch ? (
                        <DateOnly date={record.dateOfDispatch} />
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </h6>
                  </div>
                  <div>
                    <p className="mb-1 text-uppercase fw-medium small">
                      Handed back to client
                    </p>
                    <h6 className="mb-0 fs-14">
                      {record.dateHandedBackToClient ? (
                        <DateOnly date={record.dateHandedBackToClient} />
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </h6>
                  </div>
                  {record.cancelledAt ? (
                    <div>
                      <p className="mb-1 text-uppercase fw-medium small text-danger">
                        Cancelled
                      </p>
                      <h6 className="mb-0 fs-14 text-danger">
                        <DateTime date={record.cancelledAt} />
                      </h6>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <h6 className="text-uppercase text-muted mb-3">Client</h6>
                <div className="vstack gap-2">
                  <Link
                    to={`/clients/${record.clientId}`}
                    className="fw-semibold link"
                  >
                    {record.client?.name?.trim() || `Client #${record.clientId}`}
                  </Link>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={cancelOpen} toggle={() => !busy && setCancelOpen(false)} centered>
        <ModalHeader toggle={() => !busy && setCancelOpen(false)}>
          Cancel complaint
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            This marks the complaint as cancelled. This cannot be undone.
          </p>
          <Label className="form-label" for="cm-cancel-reason">
            Reason <span className="text-muted fw-normal">(optional)</span>
          </Label>
          <Input
            id="cm-cancel-reason"
            type="textarea"
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button color="light" onClick={() => setCancelOpen(false)} disabled={busy}>
              Back
            </Button>
            <Button color="danger" disabled={busy} onClick={() => void handleCancel()}>
              {busy ? "Cancelling…" : "Cancel complaint"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <DeleteModal
        show={deleteOpen}
        onDeleteClick={() => void handleDelete()}
        onCloseClick={() => setDeleteOpen(false)}
        recordId={itemLabel}
      />
    </div>
  );
};

export default ComplaintMaterialOverview;
